import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import DS from 'ember-data';
import { storageFor } from 'ember-local-storage';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import AuthService from 'cio/services/auth';
import FeaturesService from 'ember-feature-flags/services/features';
import MessageSettingsService from 'cio/services/message-settings';
import EnvironmentService from 'cio/services/environment';
import orderBy from 'lodash.orderby';
import {
  USER_ROLE_ADMIN,
  USER_ROLE_AUTHOR,
  USER_ROLE_AUTHOR_REDACT,
  USER_ROLE_MANAGER,
  USER_ROLE_VIEWER_REDACT,
} from 'cio/utils/user-roles';
import { WorkspaceIntegrationCardArgs } from 'cio/components/workspace-integration-card';
import AccountService from 'cio/services/account';

export default class WorkspaceSettingsIndexController extends Controller {
  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service features!: FeaturesService;
  @service messageSettings!: MessageSettingsService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  @storageFor('cio-alerts') localStorage: any;

  @computed('auth.user.role')
  get isApiCredentialsAccessible(): boolean {
    return [USER_ROLE_ADMIN, USER_ROLE_MANAGER, USER_ROLE_AUTHOR, USER_ROLE_AUTHOR_REDACT].includes(
      this.auth.user?.role ?? USER_ROLE_VIEWER_REDACT, // if there is no user role, default to viewer_redacted
    );
  }

  get languageSettingsEnabled() {
    return this.environment.current.hasLanguageAttribute;
  }

  get isCustomObjectEnabled() {
    return this.store.peekAll('object-type').toArray().length > 0;
  }

  get isAtLeastOneCustomObjectEnabled() {
    return this.store
      .peekAll('object-type')
      .toArray()
      .some((type) => type.enabled);
  }

  get subscriptionCentreEnabled() {
    return this.environment.current.subscriptionCenterEnabled;
  }

  get shouldDisplayJourneysSettings() {
    return this.account.isJourneysAvailable;
  }

  get advancedSettingsIntegrationCards() {
    interface Card extends Omit<WorkspaceIntegrationCardArgs, 'cardType'> {
      testName: string;
      isAccessible?: boolean;
    }
    let cards: Card[] = [
      {
        testName: 'merge-options',
        icon: 'merge',
        title: 'Merge options',
        enabled: true,
        hideEnabledLabel: true,
        description: 'Choose when we automatically merge data for you',
        routeName: 'workspace-settings.merge_options',
      },
      {
        testName: 'message-limit',
        icon: 'stop',
        title: 'Message limit',
        enabled: this.messageSettings.isMessagingLimitEnabled,
        isLoading: this.messageSettings.isMessagingLimitLoading,
        description: 'Set a max number of messages a person can receive in a given time period',
        routeName: 'actions.messaging_limit',
      },
      {
        testName: 'tz-match',
        icon: 'calendar',
        title: 'Time zone match',
        enabled: true,
        description: "Send messages in your customers' time zones",
        routeName: 'actions.time_zone_match',
      },
      {
        testName: 'language-settings',
        icon: 'localization',
        title: 'Language settings',
        enabled: this.languageSettingsEnabled,
        description: 'Connect with your customers in their language',
        routeName: 'workspace-settings.language',
      },
      {
        testName: 'url-parameters',
        icon: 'link',
        title: 'URL parameters',
        enabled: this.messageSettings.areURLParamsEnabled,
        isLoading: this.messageSettings.isURLParamsCredentialLoading,
        description: 'Customize the URL parameters appended to links in your messages',
        routeName: 'actions.url_params',
      },
      {
        testName: 'subscription-center',
        icon: 'email',
        title: 'Subscription center',
        enabled: this.subscriptionCentreEnabled,
        description: 'Let your audience choose the content they want to receive',
        routeName: 'workspace-settings.subscription_center',
      },
    ];

    if (this.isApiCredentialsAccessible) {
      cards.push({
        testName: 'api-credentials',
        icon: 'api-credentials',
        title: 'API and webhook credentials',
        enabled: true,
        hideEnabledLabel: true,
        description:
          'These credentials allow you to send information about your customers to Customer.io and verify webhook requests sent from Customer.io',
        routeName: 'workspace-settings.api_credentials',
        isAccessible: this.auth.hasPermission('account/api_credentials/view'),
      });
    }

    cards.push({
      testName: 'objects',
      icon: 'object',
      title: 'Custom objects',
      customLabel: 'New',
      showCustomLabel: !this.isCustomObjectEnabled,
      enabled: this.isCustomObjectEnabled,
      hideEnabledLabel: !this.isAtLeastOneCustomObjectEnabled,
      customLabelClass: 'fly-label--purple',
      description: 'Configure a custom data structure that can be related to people and used for advanced segmentation',
      routeName: 'workspace-settings.objects',
    });

    // ensure always-alphabetic sort in this list:
    return orderBy(cards, ['title']);
  }
}
